import { useMemo, useState } from 'react';
import Fuse from 'fuse.js';

export default function useContactSearch(contacts) {
  const [searchQuery, setSearchQuery] = useState('');

  // Deduplicate contacts based on image_url (unique identifier)
  const uniqueContacts = useMemo(() => {
    const seen = new Set();
    return contacts.filter((contact) => {
      const identifier = contact.image_url;
      if (!identifier) {
        return true; // Keep contacts without image_url (edge case)
      }
      if (seen.has(identifier)) {
        return false; // Skip duplicate
      }
      seen.add(identifier);
      return true;
    });
  }, [contacts]);

  // Sort contacts by last_updated (newest first) for default display
  const sortedContacts = useMemo(() => {
    return [...uniqueContacts].sort((a, b) => {
      const dateA = new Date(a.last_updated || 0);
      const dateB = new Date(b.last_updated || 0);
      return dateB - dateA; // DESC order (newest first)
    });
  }, [uniqueContacts]);

  // Initialize Fuse.js with weighted search configuration
  const fuse = useMemo(() => {
    return new Fuse(sortedContacts, {
      keys: [
        { name: 'full_name', weight: 0.4 }, // 40% weight - most important
        { name: 'email', weight: 0.2 }, // 20% weight
        { name: 'organization', weight: 0.2 }, // 20% weight
        { name: 'position', weight: 0.1 }, // 10% weight
        { name: 'phone_number', weight: 0.05 }, // 5% weight
        { name: 'address', weight: 0.05 }, // 5% weight
      ],
      threshold: 0.4, // 0.0 = perfect match, 1.0 = match anything
      distance: 100, // Max distance for fuzzy matching
      includeScore: true, // Return relevance scores
      minMatchCharLength: 2, // Start searching after 2 characters
      shouldSort: true, // Auto-sort by score (best matches first)
      ignoreLocation: true, // Search in entire string
    });
  }, [sortedContacts]);

  // Filter and rank contacts using Fuse.js fuzzy search
  const filteredContacts = useMemo(() => {
    const query = searchQuery.trim();

    // If no search query, return contacts in default order
    if (!query || query.length < 2) {
      return sortedContacts;
    }

    // Use Fuse.js to search and rank results
    const results = fuse.search(query);

    // Extract contact items from Fuse results
    // Results are already sorted by relevance score
    return results.map((result) => result.item);
  }, [searchQuery, sortedContacts, fuse]);

  return {
    searchQuery,
    setSearchQuery,
    filteredContacts,
  };
}

